import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext } from "react";
import { APIError, api, jsonBody } from "../api/client";
import type { AuthSession } from "../api/types";

export type SignInResult =
  | { mfaRequired: true; expiresAt: string }
  | { mfaRequired: false; session: AuthSession };

interface MFAChallengeResponse {
  mfaRequired: true;
  expiresAt: string;
}

interface AuthContextValue {
  session: AuthSession | null;
  loading: boolean;
  signIn(email: string, password: string): Promise<SignInResult>;
  completeMFA(value: { code?: string; recoveryCode?: string }): Promise<AuthSession>;
  selectTenant(tenantId: string): Promise<AuthSession>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
export const sessionKey = ["auth", "session"] as const;

// Whether the last visit ended signed in, kept aside so the very first paint can choose
// between the full application frame and the plain boundary card before the session
// answer arrives. Storage failures are swallowed: the guess simply falls back to the
// quiet card, and the real session decides the layout a moment later.
const sessionHintKey = "venueops-has-session";

function rememberSessionHint(present: boolean) {
  try {
    if (present) {
      localStorage.setItem(sessionHintKey, "1");
    } else {
      localStorage.removeItem(sessionHintKey);
    }
  } catch {
    // Private browsing or blocked storage; the hint just will not survive a reload.
  }
}

/** True when the previous visit on this browser ended with a signed-in session. */
export function hasSessionHint(): boolean {
  try {
    return localStorage.getItem(sessionHintKey) === "1";
  } catch {
    return false;
  }
}

async function loadSession(): Promise<AuthSession | null> {
  try {
    const session = await api<AuthSession>("/api/v1/auth/session");
    rememberSessionHint(true);
    return session;
  } catch (error) {
    if (error instanceof APIError && error.status === 401) {
      rememberSessionHint(false);
      return null;
    }
    throw error;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({ queryKey: sessionKey, queryFn: loadSession, retry: false });

  const value: AuthContextValue = {
    session: sessionQuery.data ?? null,
    loading: sessionQuery.isLoading,
    async signIn(email, password) {
      const result = await api<AuthSession | MFAChallengeResponse>("/api/v1/auth/login", {
        method: "POST",
        body: jsonBody({ email, password }),
      });
      if ("mfaRequired" in result && result.mfaRequired) {
        rememberSessionHint(false);
        queryClient.setQueryData(sessionKey, null);
        return { mfaRequired: true, expiresAt: result.expiresAt };
      }
      const session = result as AuthSession;
      rememberSessionHint(true);
      queryClient.setQueryData(sessionKey, session);
      return { mfaRequired: false, session };
    },
    async completeMFA(challenge) {
      const session = await api<AuthSession>("/api/v1/auth/login/mfa", {
        method: "POST",
        body: jsonBody(challenge),
      });
      rememberSessionHint(true);
      queryClient.setQueryData(sessionKey, session);
      return session;
    },
    async selectTenant(tenantId) {
      const session = await api<AuthSession>("/api/v1/auth/select-tenant", {
        method: "POST",
        body: jsonBody({ tenantId }),
      });
      rememberSessionHint(true);
      queryClient.setQueryData(sessionKey, session);
      await queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
      return session;
    },
    async signOut() {
      await api<void>("/api/v1/auth/logout", { method: "POST" });
      rememberSessionHint(false);
      queryClient.setQueryData(sessionKey, null);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
    },
    async refresh() {
      await queryClient.invalidateQueries({ queryKey: sessionKey });
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
